import React, {useState, useContext, useEffect} from 'react';
import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {SA_sendCompanyWelcomeMessage} from "../../../../Services/SA_api";
import {getUserAPI} from "../../../../Services/api";
import {get} from "lodash";
import {MessageTypes} from "../../../../Utils/Constants";
import {GridReactContext} from "../../../../Utils/DataGridWrapper/GridContext";
import {Modal} from "../../../Smart/Modal/Modal";
import {MessageTypeSelectionButtons} from "../../../Notifications/MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import './SendWelcomeMessageCol.scss';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';

/**
 * Component for sending welcome messages to all users in a company
 * Shows message delivery method selection modal before sending and displays sent message details when already sent
 */
export const SendWelcomeMessageCol = ({company}) => {
    const {i18n} = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);
    const [isSending, setIsSending] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedMessagingTypes, setSelectedMessagingTypes] = useState([MessageTypes.SMS]);
    const [sentByUserName, setSentByUserName] = useState('');
    const [isLoadingUserName, setIsLoadingUserName] = useState(false);
    
    // Check if welcome message has already been sent
    const hasBeenSent = get(company, 'additionalData.sentFirstWelcomeMessage', false);
    const sentDate = get(company, 'additionalData.sentFirstWelcomeMessageDate');
    const sentByUserId = get(company, 'additionalData.sentFirstWelcomeMessageByUser');

    const fetchSentByUserName = async ({userId}) => {
        if (!userId) return;
        
        setIsLoadingUserName(true);
        try {
            const user = await getUserAPI({userId});
            const fullName = `${get(user, 'firstName', '')} ${get(user, 'lastName', '')}`.trim();
            setSentByUserName(fullName || userId); // Fallback to userId if name not available
        } catch (error) {
            console.error('Error fetching user details:', error);
            setSentByUserName(userId); // Fallback to userId on error
        } finally {
            setIsLoadingUserName(false);
        }
    };

    // Fetch user name when details modal opens and we have a user ID
    useEffect(() => {
        if (showDetailsModal && sentByUserId && !sentByUserName) {
            fetchSentByUserName({userId: sentByUserId});
        }
    }, [showDetailsModal, sentByUserId, sentByUserName]);
    
    const handleButtonClick = () => {
        if (hasBeenSent) {
            setShowDetailsModal(true);
        } else {
            setShowSendModal(true);
        }
    };
    
    const handleSendWelcomeMessage = async () => {
        setShowSendModal(false);
        setIsSending(true);
        try {
            await SA_sendCompanyWelcomeMessage({
                companyId: company.id,
                messageTypes: selectedMessagingTypes
            });
            
            // Refresh the table to show updated status
            gridContext.triggerTableRefresh();
        } catch (error) {
            console.error('Error sending welcome message:', error);
        } finally {
            setIsSending(false);
        }
    };

    const onSelectedMessagingTypeChange = (messagingType) => {
        //if it is the only one selected do not remove it
        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType));
        } else {
            let messagingTypes = [...selectedMessagingTypes, messagingType];
            //uniq array
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index);
            setSelectedMessagingTypes(messagingTypes);
        }
    };
    
    return (
        <div className="SendWelcomeMessageCol">
            <Button 
                color={hasBeenSent ? get(buttonColors, 'GREY') : get(buttonColors, 'GOLD')}
                size="small"
                text={hasBeenSent ? 
                    i18n.translate('superAdmin.customersTable.columns.welcomeMessageSent') : 
                    i18n.translate('superAdmin.customersTable.columns.welcomeMessage')
                }
                onClick={handleButtonClick}
                disabled={isSending}
            />
            
            {/* Send Welcome Message Modal */}
            {showSendModal && (
                <Modal
                    open={showSendModal}
                    onClose={() => setShowSendModal(false)}
                >
                    <div className="SendWelcomeMessageModal modalWrapper">
                        <div className="modalTitle">
                            <div>{i18n.translate('superAdmin.customersTable.welcomeMessage.confirmTitle')} {get(company, 'companyName' ,'')}</div>
                        </div>
                        <div className="modalContent">
                            <div className="companyInfo">
                                <strong>{i18n.translate('superAdmin.customersTable.welcomeMessage.company')}</strong> {get(company, 'companyName' ,'')}
                            </div>
                            <div className="messageDeliverySelection">
                                <MessageTypeSelectionButtons 
                                    onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                                    selectedMessagingTypes={selectedMessagingTypes}
                                    isSuperAdmin={true}
                                />
                            </div>
                        </div>
                        <div className="modalFooter">
                            <Button 
                                color={buttonColors.GREY}
                                text={i18n.translate('general.cancel')}
                                onClick={() => setShowSendModal(false)}
                            />
                            <Button 
                                color={buttonColors.YELLOW}
                                text={i18n.translate('sendSMSModal.send')}
                                onClick={handleSendWelcomeMessage}
                                disabled={selectedMessagingTypes.length === 0}
                            />
                        </div>
                    </div>
                </Modal>
            )}
            
            {/* Details Modal */}
            {showDetailsModal && (
                <Modal
                    open={showDetailsModal}
                    onClose={() => setShowDetailsModal(false)}
                >
                 {isLoadingUserName ? <LoadingSpinner/> :  <div className="SendWelcomeMessageDetailsModal modalWrapper">
                        <div className="modalTitle">
                            <div>{i18n.translate('superAdmin.customersTable.welcomeMessage.detailsTitle')}</div>
                        </div>
                        <div className="modalContent">
                            <div className="detailRow">
                                <strong>{i18n.translate('superAdmin.customersTable.welcomeMessage.company')}</strong> {get(company, 'companyName' ,'')}
                            </div>
                            {sentDate && (
                                <div className="detailRow">
                                    <strong>{i18n.translate('superAdmin.customersTable.welcomeMessage.sentDate')}</strong> {new Date(sentDate).toLocaleDateString()}
                                </div>
                            )}
                            {sentByUserId && (
                                <div className="detailRow">
                                    <strong>{i18n.translate('superAdmin.customersTable.welcomeMessage.sentBy')}</strong> 
                                    {sentByUserName || sentByUserId}
                                </div>
                            )}
                        </div>
                        <div className="modalFooter">
                            <Button 
                                color={buttonColors.YELLOW}
                                text={i18n.translate('general.close')}
                                onClick={() => setShowDetailsModal(false)}
                            />
                        </div>
                    </div>}
                </Modal>
            )}
        </div>
    );
}; 